import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BarChart3, AlertTriangle, Bot, Inbox, Download, Loader2 } from "lucide-react";
import HelpdeskSuiteLayout from "../../../layouts/helpdeskLayout/HelpdeskSuiteLayout";
import { cn } from "../../../utils/cn";
import { PageHeader } from "../../../components/ui/PageHeader";
import { Card } from "../../../components/ui/Card";
import { Skeleton } from "../../../components/ui/Skeleton";
import { EmptyState } from "../../../components/ui/EmptyState";
import { PendingBackendNotice } from "../../../components/ui/PendingBackendNotice";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/Tabs";
import { useUrlState } from "../../../hooks/useUrlState";
import { getReportsSummary, downloadCasesCsv, isEndpointMissing } from "./reports/reportsApi";
import { localYMD, formatRangeLabel } from "./reports/format";
import RangePicker from "./reports/RangePicker";
import KpiCards from "./reports/KpiCards";
import DailyTrafficChart from "./reports/DailyTrafficChart";
import HourlyChart from "./reports/HourlyChart";
import ChannelChart from "./reports/ChannelChart";
import FolderDistribution from "./reports/FolderDistribution";
import AgentTable from "./reports/AgentTable";
import AgentCasesDrawer from "./reports/AgentCasesDrawer";
import AiUsagePanel from "./reports/AiUsagePanel";

// Reportes del helpdesk: KPIs y agregados de conversaciones/mensajes por rango
// de fechas, servidos por GET /helpdesk/reports/summary en UNA sola llamada.
//
// El microservicio de reportes puede NO estar desplegado aún: igual que
// QuickRepliesSettings, la vista distingue backend ausente (404/405 ⇒
// PendingBackendNotice, navegable en vacío), error real (card con reintento) y
// período sin actividad (EmptyState con el selector de rango operable).
//
// Dos pestañas: el reporte de conversaciones y el consumo/costo de la IA. Cada
// una tiene su endpoint y su propio estado de carga — que falte el de IA no
// puede vaciar el reporte, ni al revés.

const PRESET_DAYS = { "7d": 7, "30d": 30, "90d": 90 };

// Pestaña activa. En la URL (?tab=ia) y con push —igual que AiSettings— para
// que back/forward vuelva a la pestaña anterior; sin `tab` se asume el reporte,
// que es el destino natural de /helpdesk/reports.
const TAB_REPORTS = "reports";
const TAB_IA = "ia";

// Referencias estables para props de componentes memoizados: un literal nuevo
// por render invalidaría el memo de cada chart (regla PERF del helpdesk).
const EMPTY_ROWS = [];
const EMPTY_KPIS = {};

/**
 * Deriva el rango efectivo. Presets: hoy hacia atrás (30d = hoy y los 29 días
 * anteriores). from = inicio de día LOCAL y to = fin de día LOCAL, enviados
 * como ISO: el backend agrega por día del tenant, no por día UTC.
 * @param {string} preset - '7d' | '30d' | '90d' | 'custom'
 * @param {string} customFrom - YYYY-MM-DD (solo válido, lo garantiza RangePicker)
 * @param {string} customTo - YYYY-MM-DD
 * @returns {{fromYMD: string, toYMD: string, fromISO: string, toISO: string}}
 */
function computeRange(preset, customFrom, customTo) {
  let fromYMD;
  let toYMD;
  if (preset === "custom" && customFrom && customTo) {
    fromYMD = customFrom;
    toYMD = customTo;
  } else {
    const days = PRESET_DAYS[preset] || 30;
    const today = new Date();
    const start = new Date(today);
    start.setDate(start.getDate() - (days - 1));
    fromYMD = localYMD(start);
    toYMD = localYMD(today);
  }
  return {
    fromYMD,
    toYMD,
    fromISO: new Date(`${fromYMD}T00:00:00`).toISOString(),
    toISO: new Date(`${toYMD}T23:59:59.999`).toISOString(),
  };
}

// Skeleton que espeja el layout real (7 KPIs + 2 charts + 2 charts + tabla):
// al llegar los datos nada salta de sitio. Sin spinners globales.
function ReportsSkeleton() {
  return (
    <div className="space-y-5" aria-hidden="true">
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        {Array.from({ length: 7 }).map((_, i) => (
          <Skeleton key={i} className="h-[104px] rounded-xl" />
        ))}
      </div>
      <div className="grid gap-4 xl:grid-cols-3">
        <Skeleton className="h-[280px] rounded-xl xl:col-span-2" />
        <Skeleton className="h-[280px] rounded-xl" />
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <Skeleton className="h-[220px] rounded-xl" />
        <Skeleton className="h-[220px] rounded-xl" />
      </div>
      <Skeleton className="h-[240px] rounded-xl" />
    </div>
  );
}

export default function HelpdeskReports() {
  const { t } = useTranslation();

  // Pestaña vigente. Un `?tab=` desconocido cae al reporte en vez de dejar la
  // página en blanco.
  const [tab, setTab] = useUrlState("tab", TAB_REPORTS, { push: true });
  const activeTab = tab === TAB_IA ? TAB_IA : TAB_REPORTS;
  const isIaTab = activeTab === TAB_IA;
  const handleTabChange = useCallback((next) => setTab(next), [setTab]);

  // Rango seleccionado. customFrom/To solo llegan válidos (RangePicker valida
  // from ≤ to y ≤92 días antes de propagar).
  const [preset, setPreset] = useState("30d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  // null = todavía sin respuesta (primera carga, error o backend ausente).
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [backendMissing, setBackendMissing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Export CSV del rango completo y drill-down por agente (contrato 3).
  const [csvState, setCsvState] = useState("idle"); // idle|downloading|error
  const [selectedAgentId, setSelectedAgentId] = useState(null);

  const { fromYMD, toYMD, fromISO, toISO } = useMemo(
    () => computeRange(preset, customFrom, customTo),
    [preset, customFrom, customTo],
  );

  // Guard de carreras: al cambiar rápido de rango, una respuesta vieja que
  // llegue después de la nueva NO debe pisar los datos vigentes.
  const reqRef = useRef(0);
  // Ref (no estado) para decidir skeleton vs overlay sin meter `data` en las
  // deps del efecto de carga, lo que dispararía un fetch extra tras cada éxito.
  const hasDataRef = useRef(false);

  useEffect(() => {
    let alive = true;
    const id = ++reqRef.current;
    // Con datos previos se mantienen visibles atenuados (overlay) en vez de
    // volver al skeleton: evita el salto de layout en cada cambio de rango.
    if (hasDataRef.current) setRefreshing(true);
    else setLoading(true);
    setLoadError(null);

    (async () => {
      try {
        const res = await getReportsSummary({ from: fromISO, to: toISO });
        if (!alive || reqRef.current !== id) return;
        setData(res);
        hasDataRef.current = true;
        setBackendMissing(false);
      } catch (err) {
        if (!alive || reqRef.current !== id) return;
        if (isEndpointMissing(err)) {
          // Ruta aún no desplegada: vista navegable en vacío, sin error rojo.
          setBackendMissing(true);
          setData(null);
          hasDataRef.current = false;
        } else {
          setLoadError(err?.message || t("No se pudo cargar el reporte."));
        }
      } finally {
        if (alive && reqRef.current === id) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [fromISO, toISO, reloadKey, t]);

  // Handlers estables: RangePicker no debe re-renderizarse por identidad nueva.
  const handlePreset = useCallback((key) => setPreset(key), []);
  const handleCustom = useCallback((from, to) => {
    setPreset("custom");
    setCustomFrom(from);
    setCustomTo(to);
  }, []);
  const handleRetry = useCallback(() => setReloadKey((k) => k + 1), []);

  // Descarga del CSV de casos del rango vigente. El error se auto-limpia a los
  // 4 s; el timer se corta al desmontar para no setear estado en un componente
  // muerto.
  const csvTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(csvTimerRef.current), []);
  const handleDownloadCsv = useCallback(async () => {
    setCsvState("downloading");
    try {
      await downloadCasesCsv({ from: fromISO, to: toISO });
      setCsvState("idle");
    } catch {
      setCsvState("error");
      clearTimeout(csvTimerRef.current);
      csvTimerRef.current = setTimeout(() => setCsvState("idle"), 4000);
    }
  }, [fromISO, toISO]);

  // Referencias estables hacia AgentTable/AgentCasesDrawer (ambos memoizados).
  const handleSelectAgent = useCallback((id) => setSelectedAgentId(id), []);
  const handleCloseDrawer = useCallback(() => setSelectedAgentId(null), []);

  const kpis = data?.kpis || EMPTY_KPIS;
  const isEmpty = !!data && !backendMissing && (kpis.total || 0) === 0;
  const showContent = !loading && !loadError && (data || backendMissing);

  return (
    // Sin activeKey: el rail deriva el ítem activo del pathname
    // (/helpdesk/reports); el ítem "Reportes" lo cablea F1 en HelpdeskRail.
    <HelpdeskSuiteLayout>
      <div className="flex flex-col h-full min-h-0 bg-bg">
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-6xl mx-auto px-5 py-5 space-y-5">
            <PageHeader
              icon={
                <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg bg-brand-50 text-brand-600 shrink-0">
                  <BarChart3 className="w-5 h-5" aria-hidden="true" />
                </span>
              }
              title={t("Reportes")}
              // El rango y la descarga son del reporte de conversaciones: en la
              // pestaña de IA la ventana la elige el propio panel (7/30 días) y
              // un segundo selector con otro rango sería contradictorio.
              subtitle={
                isIaTab
                  ? t("Consumo y costo estimado de los agentes de IA.")
                  : formatRangeLabel(fromYMD, toYMD)
              }
              actions={
                isIaTab ? undefined : (
                  <>
                  {/* Botón secundario a ras del RangePicker. md:h-[42px]
                      replica la altura TOTAL del grupo de chips (h-8 + p-1 +
                      border = 42px) para que botón y selector midan lo mismo
                      (antes el botón era h-8=32px y quedaba 10px más bajo).
                      self-start ancla ambas columnas arriba: el <p> de error
                      (h-4) queda debajo reservando su alto sin empujar. Texto
                      igual al de los chips (12px/11px). */}
                  <div className="flex flex-col items-stretch sm:items-end gap-1 self-start">
                    <button
                      type="button"
                      onClick={handleDownloadCsv}
                      disabled={
                        loading || backendMissing || !data || csvState === "downloading"
                      }
                      className={cn(
                        "inline-flex items-center justify-center gap-1.5 h-11 md:h-[42px] px-3 rounded-md text-[12px] md:text-[11px] font-medium",
                        "border border-border bg-transparent text-fg hover:bg-bg-subtle",
                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors",
                        "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent",
                      )}
                    >
                      {csvState === "downloading" ? (
                        <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                      ) : (
                        <Download className="w-4 h-4" aria-hidden="true" />
                      )}
                      {t("Descargar CSV")}
                    </button>
                    <p
                      className={cn(
                        "text-[12px] md:text-[11px] leading-4 h-4 text-status-error text-right",
                        csvState !== "error" && "invisible",
                      )}
                      role={csvState === "error" ? "alert" : undefined}
                    >
                      {t("No se pudo descargar el CSV.")}
                    </p>
                  </div>
                  <RangePicker
                    preset={preset}
                    appliedFrom={fromYMD}
                    appliedTo={toYMD}
                    onSelectPreset={handlePreset}
                    onApplyCustom={handleCustom}
                  />
                  </>
                )
              }
            />

            {/* Barra fina de progreso durante el refresco de rango. El alto se
                reserva siempre para que el contenido no salte al aparecer. */}
            <div className="h-0.5 -mt-2" aria-hidden="true">
              {refreshing && (
                <div className="h-0.5 w-full rounded-full bg-brand-500/70 animate-pulse motion-reduce:animate-none" />
              )}
            </div>
            <p className="sr-only" role="status" aria-live="polite">
              {refreshing ? t("Actualizando el reporte…") : ""}
            </p>

            <Tabs value={activeTab} onValueChange={handleTabChange}>
              <TabsList ariaLabel={t("Secciones del reporte")}>
                <TabsTrigger
                  value={TAB_REPORTS}
                  icon={<BarChart3 className="w-3.5 h-3.5" aria-hidden="true" />}
                >
                  {t("Conversaciones")}
                </TabsTrigger>
                <TabsTrigger
                  value={TAB_IA}
                  icon={<Bot className="w-3.5 h-3.5" aria-hidden="true" />}
                >
                  {t("IA")}
                </TabsTrigger>
              </TabsList>

              <TabsContent value={TAB_REPORTS} className="pt-4 space-y-5">
                {backendMissing && (
                  <PendingBackendNotice>
                    {t(
                      "Los reportes están en construcción. Puedes recorrer la vista, pero las métricas aparecerán cuando estén disponibles.",
                    )}
                  </PendingBackendNotice>
                )}

                {loading && <ReportsSkeleton />}

                {!loading && loadError && (
                  <Card padding="lg" className="max-w-md mx-auto text-center">
                    {/* Mismo patrón que el bloque fetchError de AgentPicker. */}
                    <div className="flex items-center justify-center gap-1.5 text-xs text-status-error">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                      {loadError}
                    </div>
                    <button
                      type="button"
                      onClick={handleRetry}
                      className="mt-2 h-8 px-3 rounded-md text-xs font-medium text-brand-700 hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors"
                    >
                      {t("Reintentar")}
                    </button>
                  </Card>
                )}

                {showContent && isEmpty && (
                  <div className="rounded-xl border border-border bg-bg">
                    {/* El RangePicker del header sigue operable: la salida natural
                        de un período vacío es elegir otro período. */}
                    <EmptyState
                      icon={<Inbox aria-hidden="true" />}
                      title={t("Sin actividad en el período seleccionado")}
                      description={t(
                        "No hubo conversaciones ni mensajes entre esas fechas. Prueba con un rango más amplio.",
                      )}
                    />
                  </div>
                )}

                {showContent && !isEmpty && (
                  <div
                    className={cn(
                      "space-y-5",
                      // Cambio de rango: datos previos atenuados e inertes, sin
                      // volver al skeleton (evita saltos de layout).
                      refreshing && "opacity-60 pointer-events-none",
                    )}
                    aria-busy={refreshing}
                  >
                    <KpiCards
                      total={kpis.total}
                      abiertas={kpis.abiertas}
                      pendientes={kpis.pendientes}
                      resueltas={kpis.resueltas}
                      casosNuevos={kpis.casos_nuevos}
                      primeraRespuestaMin={kpis.tiempo_primera_respuesta_avg_min}
                      resolucionMin={kpis.tiempo_resolucion_avg_min}
                    />

                    <div className="grid gap-4 xl:grid-cols-3">
                      <DailyTrafficChart
                        className="xl:col-span-2"
                        rows={data?.por_dia || EMPTY_ROWS}
                        fromYMD={fromYMD}
                        toYMD={toYMD}
                      />
                      <HourlyChart rows={data?.por_hora || EMPTY_ROWS} />
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <ChannelChart rows={data?.por_canal || EMPTY_ROWS} />
                      <FolderDistribution rows={data?.por_carpeta || EMPTY_ROWS} />
                    </div>

                    <AgentTable
                      rows={data?.por_agente || EMPTY_ROWS}
                      onSelectAgent={handleSelectAgent}
                    />
                  </div>
                )}
              </TabsContent>

              {/* Endpoint y estados propios: que falte el microservicio de
                  reportes no puede dejar esta pestaña vacía (ni al revés). */}
              <TabsContent value={TAB_IA} className="pt-4">
                <AiUsagePanel />
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* Drill-down de casos del agente clickeado. Montado siempre (open lo
            gobierna) y SOLO con props primitivos + callbacks estables: es un
            componente memoizado (regla PERF). */}
        <AgentCasesDrawer
          open={!!selectedAgentId}
          agentId={selectedAgentId}
          fromISO={fromISO}
          toISO={toISO}
          onClose={handleCloseDrawer}
        />
      </div>
    </HelpdeskSuiteLayout>
  );
}
